/**
 * Grade Team session probe (v3.80.0) — "does this teacher own a class at a
 * building whose registry doc has Grade Team turned on?", answered ONCE per
 * session so the nav entry and the route can be gated without a read per
 * render:
 *   1. the teacher's live current-year classes — the ONE buildMyClassesQuery
 *      the roster-lock probe already caches (rosterSource.getMyLiveClasses);
 *   2. one cached `schools/{id}` getDoc per distinct schoolId among the
 *      classes the teacher OWNS (teacherId == uid) that carry both a
 *      `schoolId` and a `grade` — hand-built classes carry neither and can
 *      never qualify (BACKLOG "NON-CLEVER CLASSES INVISIBLE TO TLS").
 * Result: the qualifying buildings with the grades the teacher teaches there.
 * Cleared on sign-out (App.js) and when the callable answers
 * permission-denied (the setting may have been turned off mid-session).
 *
 * Budget: docs/perf/PERFORMANCE.md "App shell — Clever roster-lock nav probe"
 * (+1 schools get per distinct building, usually 1).
 */
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getMyLiveClasses } from './rosterSource';
import { gradeKeyOf, NO_GRADE } from './reportCore';
import { compareGrades } from './schoolView';

const buildingsCache = new Map(); // uid → Promise<buildings[]>
const schoolCache = new Map();    // schoolId → Promise<{ schoolId, name, on }>

/** The registry setting as the callable reads it: 'on' or nothing. */
export const isGradeTeamOn = (schoolData) => !!(schoolData && schoolData.settings && schoolData.settings.gradeTeamView === 'on');

function getSchoolSetting(schoolId) {
  if (!schoolCache.has(schoolId)) {
    const pending = getDoc(doc(db, 'schools', schoolId))
      .then((snap) => {
        const data = snap.exists() ? snap.data() : null;
        return { schoolId, name: (data && data.name) || schoolId, on: isGradeTeamOn(data) };
      })
      .catch((err) => {
        schoolCache.delete(schoolId);
        throw err;
      });
    schoolCache.set(schoolId, pending);
  }
  return schoolCache.get(schoolId);
}

/**
 * Pure: the teacher's OWNED, graded, building-stamped classes → distinct
 * buildings with the grades + class ids taught there.
 */
export function ownedBuildingsFrom(uid, classes = []) {
  const byBuilding = new Map();
  classes.forEach((c) => {
    if (!c || c.teacherId !== uid) return;
    if (typeof c.schoolId !== 'string' || !c.schoolId) return;
    const grade = gradeKeyOf(c);
    if (grade === NO_GRADE) return;
    const b = byBuilding.get(c.schoolId) || { schoolId: c.schoolId, grades: new Set(), classIds: [] };
    b.grades.add(grade);
    b.classIds.push(c.id);
    byBuilding.set(c.schoolId, b);
  });
  return [...byBuilding.values()].map((b) => ({ schoolId: b.schoolId, grades: [...b.grades].sort(compareGrades), classIds: b.classIds }));
}

/**
 * @returns {Promise<Array<{ schoolId, name, grades: string[], classIds: string[] }>>}
 *   the buildings where the setting is ON and the teacher owns a graded class;
 *   [] on any failure (a probe failure must never widen the nav — the
 *   callable is the real fence).
 */
export function getGradeTeamBuildings(user) {
  const uid = user?.uid;
  if (!uid) return Promise.resolve([]);
  if (!buildingsCache.has(uid)) {
    const pending = (async () => {
      const classes = await getMyLiveClasses(user);
      const owned = ownedBuildingsFrom(uid, classes);
      const settings = await Promise.all(owned.map((b) => getSchoolSetting(b.schoolId)));
      return owned
        .map((b, i) => ({ ...b, name: settings[i].name, on: settings[i].on }))
        .filter((b) => b.on)
        .map(({ on, ...b }) => b);
    })().catch((err) => {
      buildingsCache.delete(uid);
      console.error('getGradeTeamBuildings: probe failed — Grade Team stays hidden this session', err);
      return [];
    });
    buildingsCache.set(uid, pending);
  }
  return buildingsCache.get(uid);
}

/** Sign-out hook (App.js) and the permission-denied recovery on the page. */
export function clearGradeTeamSourceCache() {
  buildingsCache.clear();
  schoolCache.clear();
}
